import { useGetStations } from "@/entities/station";
import { BeheerVestiging, useDeleteVestiging } from "@/entities/vestiging";
import {
    DataTable,
    DataTableColumnHeader,
    DataTableRowActions,
    Dialog,
    DialogContainer,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/shared/components";
import type { DataTableFeatures } from "@/shared/components/table/data-table-features";
import { DataTableToolbarOptions } from "@/shared/components/table/data-table-toolbar";
import { unique } from "@/shared/utils";
import { createColumnHelper } from "@tanstack/react-table";
import { useState } from "react";
import { NewEditVestigingForm } from "./newedit-vestiging-form";

const weekdaysPrefixes = ["ma", "di", "wo", "do", "vr", "za", "zo"];

interface VestigingenTableProps {
    vestigingen: BeheerVestiging[];
}

export function VestigingenTable({ vestigingen }: Readonly<VestigingenTableProps>) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedVestiging, setSelectedVestiging] = useState<BeheerVestiging | null>(null);
    const { data: stations, isPending } = useGetStations();
    const deleteVestiging = useDeleteVestiging();

    if (isPending) {
        return <p>Laden...</p>;
    }

    const columnHelper = createColumnHelper<DataTableFeatures, BeheerVestiging>();

    const columns = columnHelper.columns([
        columnHelper.accessor("bedrijfsNaam", {
            header: ({ column }) => <DataTableColumnHeader column={column} title="Bedrijfsnaam" />,
            filterFn: "arrHas",
        }),
        columnHelper.accessor("vestigingNaam", {
            header: ({ column }) => <DataTableColumnHeader column={column} title="Vestigingnaam" />,
        }),
        columnHelper.accessor("stationCode", {
            header: ({ column }) => <DataTableColumnHeader column={column} title="Station" />,
            filterFn: "arrHas",
        }),
        columnHelper.accessor("beschikbareFaciliteiten", {
            header: ({ column }) => <DataTableColumnHeader column={column} title="Faciliteiten" />,
            cell: (context) => {
                const value = context.getValue();
                return Array.isArray(value) ? value.join(", ") : "";
            },
        }),
        columnHelper.accessor("telefoonnummer", {
            header: ({ column }) => <DataTableColumnHeader column={column} title="Telefoonnummer" />,
            cell: (context) => {
                const value = context.getValue();
                return value ?? "-";
            },
        }),
        columnHelper.accessor("isNsRetailVestiging", {
            header: ({ column }) => <DataTableColumnHeader column={column} title="NS Retail vestiging" />,
            cell: (context) => (context.getValue() ? "Ja" : "Nee"),
        }),
        columnHelper.accessor("inzetbaar", {
            header: ({ column }) => <DataTableColumnHeader column={column} title="Inzetbaar" />,
            cell: (context) => (context.getValue() ? "Ja" : "Nee"),
        }),
        columnHelper.accessor("handmatigToegevoegd", {
            header: ({ column }) => <DataTableColumnHeader column={column} title="Handmatig toegevoegd" />,
            cell: (context) => (context.getValue() ? "Ja" : "Nee"),
        }),
        columnHelper.accessor("openingstijden", {
            header: ({ column }) => <DataTableColumnHeader column={column} title="Openingstijden" />,
            cell: (context) => {
                return (
                    <ol>
                        {context.getValue().map((ot, i) => (
                            <li className="flex" key={i}>
                                <span className="basis-7">{weekdaysPrefixes[ot.dag - 1]}</span>
                                <span>{`${ot.openVan} - ${ot.openTot}`}</span>
                            </li>
                        ))}
                    </ol>
                );
            },
        }),
        columnHelper.display({
            id: "Acties",
            cell: (context) => {
                const row = context.row;
                return (
                    <DataTableRowActions
                        row={row}
                        onDelete={
                            row.original.handmatigToegevoegd
                                ? () => {
                                      deleteVestiging.mutate(row.original.id);
                                  }
                                : undefined
                        } // Places API vestigingen cannot be deleted
                        onEdit={() => {
                            setDialogOpen(true);
                            setSelectedVestiging(row.original);
                        }}
                    />
                );
            },
        }),
    ]);

    const toolbarOptions: DataTableToolbarOptions<BeheerVestiging> = {
        filters: {
            globalFilter: {
                columns: ["bedrijfsNaam", "vestigingNaam"],
                placeholder: "Zoek op naam...",
            },
            multiSelectFilters: [
                {
                    column: "stationCode",
                    title: "Station",
                    options: unique(vestigingen.map((v) => v.stationCode))
                        .map((stationCode) => {
                            const stationCodeLowerCase = stationCode.toLowerCase();
                            const station = stations?.find((s) => s.code.toLowerCase() === stationCodeLowerCase);
                            return {
                                label: station?.langeNaam ?? stationCode,
                                value: stationCode,
                            };
                        })
                        .sort((a, b) => a.label.localeCompare(b.label)),
                },
                {
                    column: "bedrijfsNaam",
                    title: "Bedrijf",
                    options: unique(vestigingen.map((v) => v.bedrijfsNaam)).map((bedrijfsnaam) => ({
                        label: bedrijfsnaam,
                        value: bedrijfsnaam,
                    })),
                },
            ],
            booleanFilters: [
                { column: "inzetbaar", title: "Inzetbaar", "data-testid": "inzetbaar" },
                { column: "handmatigToegevoegd", title: "Handmatig toegevoegd", "data-testid": "handmatig-toegevoegd" },
            ],
        },
        columnVisibility: {
            showMenu: true,
            defaultValues: {
                openingstijden: false,
            },
        },
        actions: [
            {
                label: "Nieuwe vestiging",
                onClick: () => {
                    setDialogOpen(true);
                },
                "data-testid": "nieuwe-vestiging",
            },
        ],
    };

    const onOpenChange = (open: boolean) => {
        if (!open) setSelectedVestiging(null);
        setDialogOpen(open);
    };

    return (
        <div>
            <h1>{selectedVestiging?.bedrijfsNaam}</h1>
            <DataTable
                columns={columns}
                data={vestigingen}
                toolbarOptions={toolbarOptions}
                onDoubleClick={(vestiging) => {
                    setSelectedVestiging(vestiging.original);
                    setDialogOpen(true);
                }}
            />
            <Dialog open={dialogOpen} onOpenChange={onOpenChange}>
                <DialogContainer className="min-h-[168px] min-w-1/2 sm:max-w-[540px]">
                    <DialogHeader>
                        <DialogTitle>{selectedVestiging ? "Vestiging wijzigen" : "Nieuwe vestiging"}</DialogTitle>
                        <DialogDescription className="sr-only">
                            {selectedVestiging ? "Vestiging wijzigen" : "Nieuwe vestiging toevoegen"}
                        </DialogDescription>
                    </DialogHeader>
                    <NewEditVestigingForm onOpenChange={onOpenChange} vestiging={selectedVestiging} />
                </DialogContainer>
            </Dialog>
        </div>
    );
}
